import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { FEATURE_FLAG_NAMES, useFeatureFlag, useFeatureFlagsReady } from '../hooks/useFeatureFlag';
import { useIsV2Surface } from '../utils/design';

type Scheme = 'light' | 'dark';

// The scheme the open page forces on the app; null lets the person's own setting through.
let forced: Scheme | null = null;
const listeners = new Set<() => void>();

export const setForcedColorScheme = (next: Scheme | null) => {
  if (next === forced) return;
  forced = next;
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const read = () => forced;

export const useForcedColorScheme = () => useSyncExternalStore(subscribe, read, read);

// The classic design was never drawn for dark, so a page shown in it always opens light.
export const useClassicPagesLight = () => {
  const { pathname, search } = useLocation();
  const isV2UI = useFeatureFlag(FEATURE_FLAG_NAMES.V2_UI);
  const flagsReady = useFeatureFlagsReady();
  const inV2 = useIsV2Surface(pathname, search);
  const classic = flagsReady && !(isV2UI && inV2);

  useEffect(() => setForcedColorScheme(classic ? 'light' : null), [classic]);
  useEffect(() => () => setForcedColorScheme(null), []);
};
